if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super()
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          navigation: this.querySelector('[id^="GalleryNavigation"]'),
        }
        this.mql = window.matchMedia('(min-width: 750px)')
        if (!this.elements.thumbnails) return

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        )
        this.elements.navigation
          .querySelector('[name=previous]')
          .addEventListener('click', () => {
            this.setMediaToPrevious()
          })
        this.elements.navigation
          .querySelector('[name=next]')
          .addEventListener('click', () => {
            this.setMediaToNext()
          })
        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              )
          })
        if (
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        )
          this.removeListSemantic()
        
        // Set up color filtering using shared utilities
        if (typeof ProductColorFilterUtils !== 'undefined') {
          ProductColorFilterUtils.initializeOnPageLoad(this.filterImagesByColorCode.bind(this))
          ProductColorFilterUtils.subscribe(this.filterImagesByColorCode.bind(this))
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        )
        this.setActiveThumbnail(thumbnail)
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]')
        if (!activeMedia) {
          return
        }
        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active')
          })
        activeMedia?.classList?.add('is-active')

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia)

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            )
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail)
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages()
        }

        this.preventStickyHeader()
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft })
          }
          const activeMediaRect = activeMedia.getBoundingClientRect()
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return
          const top = activeMediaRect.top + window.scrollY
          window.scrollTo({ top: top, behavior: 'smooth' })
        })
        this.playActiveMedia(activeMedia)

        if (!this.elements.thumbnails) return
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        )
        this.setActiveThumbnail(activeThumbnail)
        this.elements.navigation.querySelector('[name=previous]').disabled = !activeThumbnail.previousSibling
        this.elements.navigation.querySelector('[name=next]').disabled = !activeThumbnail.nextSibling
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        )
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'))
        thumbnail.querySelector('button').setAttribute('aria-current', true)
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft })
      }

      setMediaToNext() {
        const nextTarget = this.elements.thumbnails
          .querySelector('[aria-current="true"]')
          .parentNode.nextSibling
        if (!nextTarget) {
          return
        }
        this.setActiveMedia(nextTarget.dataset.target)
      }

      setMediaToPrevious() {
        const prevTarget = this.elements.thumbnails
          .querySelector('[aria-current="true"]')
          .parentNode.previousSibling
        if (!prevTarget) {
          return
        }
        this.setActiveMedia(prevTarget.dataset.target)
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        )
        if (!image) return
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false)
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            )
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true)
          }, 2000)
        }
        image.src = image.src
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia()
        const deferredMedia = activeItem.querySelector('.deferred-media')
        if (deferredMedia) deferredMedia.loadContent(false)
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector('sticky-header')
        if (!this.stickyHeader) return
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'))
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return
        this.elements.viewer.slider.setAttribute('role', 'presentation')
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        )
      }

      filterImagesByColorCode(colorCode) {
        const normalizedColorCode = ProductColorFilterUtils.normalizeColorCode(colorCode)

        // Filter main gallery items
        const galleryItems = this.elements.viewer.querySelectorAll('li.product__media-item')
        let firstVisibleItemId = null

        const firstVisibleItem = ProductColorFilterUtils.filterElements(galleryItems, colorCode)
        if (firstVisibleItem) {
          firstVisibleItemId = firstVisibleItem.dataset.mediaId
          // Remove is-active from hidden items
          galleryItems.forEach((item) => {
            if (item.style.display === 'none') {
              item.classList.remove('is-active')
            }
          })
        }

        // Filter thumbnail items
        if (this.elements.thumbnails) {
          const thumbnailItems = this.elements.thumbnails.querySelectorAll('[id^="Slide-Thumbnails-"]')
          ProductColorFilterUtils.filterElements(thumbnailItems, colorCode)
        }

        // Set the first visible item as active if no active item is visible
        const activeItem = this.elements.viewer.querySelector('.product__media-item.is-active')
        if (!activeItem || activeItem.style.display === 'none') {
          if (firstVisibleItem && firstVisibleItemId) {
            firstVisibleItem.classList.add('is-active')
            
            // Also update the thumbnail
            if (this.elements.thumbnails) {
              const correspondingThumb = this.elements.thumbnails.querySelector(`[data-target="${firstVisibleItemId}"]`)
              if (correspondingThumb) {
                this.elements.thumbnails.querySelectorAll('button').forEach((button) => {
                  button.removeAttribute('aria-current')
                })
                const thumbButton = correspondingThumb.querySelector('button')
                if (thumbButton) thumbButton.setAttribute('aria-current', 'true')
              }
            }
          }
        }

        // Update slider if it exists
        if (this.elements.viewer.slider) {
          this.elements.viewer.slider.update()
        }
      }
    }
  )
}
